import React from "react";
import { Grid, Card, CardContent, Skeleton, Box } from "@mui/material";
import NenhumSped from "./NenhumSped";
import ProgressoAnalise from "./ProgressoAnalise";
import Competencia from "./Competencia";
import EstablishmentSelector from '../../Components/EstablishmentSelector';
import { getToken as getAuthCookieToken } from '../../Storages/cookies';

interface SpedStatusHandlerProps {
    onSpedFiscalIdChange?: (id: number | null, dataInicio?: string, dataFim?: string, establishmentId?: number | string | null) => void;
    progresso?: number;
    statusImportacao?: string;
    isDashboard?: boolean;
}

const SpedStatusHandler: React.FC<SpedStatusHandlerProps> = ({
    onSpedFiscalIdChange,
    progresso,
    statusImportacao,
    isDashboard = false
}) => {
    const [nenhumSped, setNenhumSped] = React.useState(false);
    const [analisando, setAnalisando] = React.useState(false);
    const [spedFiscalIdSelecionado, setSpedFiscalIdSelecionado] = React.useState<number | null>(null);
    const [dataInicio, setDataInicio] = React.useState<string>('');
    const [dataFim, setDataFim] = React.useState<string>('');
    const [loading, setLoading] = React.useState(true);
    const [progressoAtual, setProgressoAtual] = React.useState(0);
    const [statusImportacaoAtual, setStatusImportacaoAtual] = React.useState('');
    const [establishmentAliasIdSelecionado, setEstablishmentAliasIdSelecionado] = React.useState<string | number>('');
    const [establishmentIdSelecionado, setEstablishmentIdSelecionado] = React.useState<string | number | null>(null);

    // Função para renderizar skeleton dos seletores
    const renderSkeleton = () => (
        <Grid item xs={12} sx={{ mt: 1 }}>
            <Grid container spacing={1} justifyContent="flex-end" alignItems="center" wrap="nowrap">
                <Grid item xs="auto">
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', minWidth: 200 }}>
                        <Skeleton variant="text" width="100%" height={24} sx={{ mb: 2 }} />
                        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                    </Box>
                </Grid>
                <Grid item xs="auto">
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', minWidth: 200 }}>
                        <Skeleton variant="text" width="100%" height={24} sx={{ mb: 2 }} />
                        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );

    const handleCompetenciaChange = React.useCallback((id: number | null, dataInicioParam?: string, dataFimParam?: string) => {
        setSpedFiscalIdSelecionado(id);
        setDataInicio(dataInicioParam || '');
        setDataFim(dataFimParam || '');
    }, []);

    const handleEstablishmentChange = React.useCallback((aliasId: string | number, establishmentId?: string | number | null) => {
        // Sempre que o estabelecimento muda, limpar o SPED selecionado para evitar requisições com IDs antigos
        setSpedFiscalIdSelecionado(null);
        setDataInicio('');
        setDataFim('');
        setAnalisando(false);

        setEstablishmentAliasIdSelecionado(aliasId);
        setEstablishmentIdSelecionado(establishmentId || null);

        // Informa imediatamente quem estiver ouvindo que o estabelecimento mudou,
        // mesmo antes de uma nova competência ser escolhida
        if (onSpedFiscalIdChange) {
            onSpedFiscalIdChange(null, undefined, undefined, establishmentId || null);
        }
    }, [onSpedFiscalIdChange]);

    const verificarProgresso = React.useCallback(async (spedId: number) => {
        try {
            const progressoResponse = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/progresso/${spedId}`);
            const progressoData = await progressoResponse.json();
            
            setProgressoAtual(progressoData.progresso || 0);
            setStatusImportacaoAtual(progressoData.status_job || '');
            
            // Se a importação foi concluída, atualiza o status
            if (progressoData.status === 'Importação concluída' || progressoData.progresso === 100) {
                setAnalisando(false);
            }
        } catch (e) {
            console.error('Erro ao verificar progresso:', e);
        }
    }, []);

    // Effect inicial para verificar se há SPEDs disponíveis
    React.useEffect(() => {
        const checkInitialStatus = async () => {
            try {
                const token =
                    getAuthCookieToken() ||
                    localStorage.getItem('token') ||
                    sessionStorage.getItem('token') ||
                    '';
                const headers: Record<string, string> = {
                    'Accept': 'application/json'
                };
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const spedResponse = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/ultimo`, {
                    headers,
                    credentials: 'include'
                });
                const sped = await spedResponse.json();

                if (sped.status === 'Nenhum SPED Fiscal importado ainda') {
                    setNenhumSped(true);
                } else {
                    setNenhumSped(false);
                }
            } catch (e) {
                console.error('[SpedStatusHandler] Erro ao carregar /sped-fiscal/ultimo', e);
            } finally {
                setLoading(false);
            }
        };

        checkInitialStatus();
    }, []);

    // Effect para verificar o status quando uma competência específica é selecionada
    React.useEffect(() => {
        if (!spedFiscalIdSelecionado) {
            setAnalisando(false);
            return;
        }

        const fetchData = async () => {
            try {
                const token =
                    getAuthCookieToken() ||
                    localStorage.getItem('token') ||
                    sessionStorage.getItem('token') ||
                    '';
                const headers: Record<string, string> = {
                    'Accept': 'application/json'
                };
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const spedResponse = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/ultimo/${spedFiscalIdSelecionado}`, {
                    headers,
                    credentials: 'include'
                });
                const sped = await spedResponse.json();

                if (sped.status === 'Realizando importação') {
                    setAnalisando(true);
                    setNenhumSped(false);
                    // Inicia a verificação do progresso
                    await verificarProgresso(spedFiscalIdSelecionado);
                } else {
                    setAnalisando(false);
                }
            } catch (e) {
                console.error('[SpedStatusHandler] Erro ao carregar /sped-fiscal/ultimo/{id}', e);
            }
        };

        fetchData();
    }, [spedFiscalIdSelecionado, verificarProgresso]);

    // Polling para atualizar progresso quando está analisando
    React.useEffect(() => {
        let intervalId: NodeJS.Timeout;
        
        if (analisando && spedFiscalIdSelecionado) {
            intervalId = setInterval(() => {
                verificarProgresso(spedFiscalIdSelecionado);
            }, 3000); // Atualiza a cada 3 segundos
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [analisando, spedFiscalIdSelecionado, verificarProgresso]);

    // Callback para o pai - só chama quando tiver um ID válido
    React.useEffect(() => {
        if (onSpedFiscalIdChange && spedFiscalIdSelecionado !== null) {
            onSpedFiscalIdChange(spedFiscalIdSelecionado, dataInicio, dataFim, establishmentIdSelecionado);
        }
    }, [spedFiscalIdSelecionado, dataInicio, dataFim, establishmentIdSelecionado, onSpedFiscalIdChange]);

    return (
        <>
            {loading ? (
                renderSkeleton()
            ) : (
                <>
                    {!nenhumSped && (
                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Grid container spacing={1} justifyContent="flex-end" alignItems="center" wrap="nowrap">
                                <Grid item xs="auto">
                                    <EstablishmentSelector disableCard onChangeEstablishment={handleEstablishmentChange} />
                                </Grid>
                                <Grid item xs="auto">
                                  <Competencia disableCard key={establishmentAliasIdSelecionado} onChangeSpedFiscalId={handleCompetenciaChange} />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {nenhumSped ? (
                        <Grid item xs={12}>
                            <NenhumSped />
                        </Grid>
                    ) : analisando ? (
                        <Grid item xs={12}>
                            <ProgressoAnalise
                                progresso={progresso ?? progressoAtual}
                                statusImportacao={statusImportacao ?? statusImportacaoAtual}
                            />
                        </Grid>
                    ) : null}
                </>
            )}
        </>
    );
};

export default SpedStatusHandler;
